// Dependencies
import React from 'react';
import radium from 'radium';
import { connect } from 'react-redux';
import _ from 'lodash';

import LeoForm from '../../../components/form/LeoForm.jsx';
import LeoInput from '../../../components/form/LeoInput.jsx';
import LeoSelect from '../../../components/form/LeoReactSelect';
import countryEnum from '../../../lib/country-enum.js';
import validate from '../../../lib/email-validation.js';
import formService from '../../../lib/form-service.js';
import { ThemeContext } from "../../../components/theme/Theme.jsx";
import { getCountryCodeFromTheme } from "../../../redux/customerReducer";
import LeoLabel from "../../../components/form/LeoLabel";


const defaultProps = {
	address: {
		countryCode: getCountryCodeFromTheme(),
		salutation: '',
		customSalutation: '',
		firstname: '',
		lastname: '',
		additionalInfo: '',
		companyName: '',
		email: '',
		postalCode: '',
		city: '',
		street: '',
		houseNumber: '',
		phoneNumber: ''
	},
};


class AddressForm extends React.Component {
	static contextType = ThemeContext;
	constructor(props) {
		super(props);

		this.state = {
			eMailInfo: null,
		};

	}

	onEmailChange(e) {
		this.props.onChange(e);
		e.persist();
		if (!this.debounce) {
			this.debounce = _.debounce(() => {
				validate(formService.formatSpecificFieldValues(e.target.name, e.target.value))
					.then((msg) => this.setState({eMailInfo: msg}));
			}, 500);
		}
		this.debounce();
	}

	render() {
		const salutations = {
			MR: 'Herr',
			MS: 'Frau',
			COMPANY: 'Firma',
		};

		const additionalDropDownValuesString =
			this.context.themeManager.value('customer-address', 'customer-address-additional-dropdown_values', null);
		const additionalDropDownValues = additionalDropDownValuesString ? additionalDropDownValuesString.split('|') : null;
		const dropDownDefaultPlaceholder = this.context.themeManager.getThemeTextOrHardcodedDefault('dropDownPlaceholder');
		const additionalDropDownPlaceholder =
			this.context.themeManager.valueOrDefault('customer-address', 'customer-address-additional-dropdown_placeholder',
				dropDownDefaultPlaceholder);

		const placeholders = formService.getPlaceholders(this.context.themeManager, 'customerAddress');

		const countriesList = Object.entries(countryEnum)
			.sort((a, b) => a[1].localeCompare(b[1]));
		const address = Object.assign({}, this.props.address || defaultProps.address);
		return (
			<LeoForm id={this.props.type + "_address_form"}>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_salutation_label"} for={this.props.type + "_salutation"} label={placeholders.salutation}/>
				<LeoSelect
					id={this.props.type + "_salutation"}
					name="salutation"
					value={address.salutation}
					valueInput={salutations[address.salutation] ? salutations[address.salutation] : ''}
					onChange={this.props.onChange}
					placeholder={placeholders.salutation}
					options={
						Object.entries(salutations).map(([key, value]) => {
							return {
								value: key,
								label: value,
								isSelected: key === address.salutation,
							}
						})
					}
					isClearable={true}
				>
				</LeoSelect>
				{this.props.showCustomSalutationField && (
					<>
						<LeoLabel className={"visually-hidden"} id={this.props.type + "_customSalutation_label"} for={this.props.type + '_customSalutation'} label={placeholders.customSalutation}/>
						<LeoInput
							type="text"
							maxLength="125"
							id={this.props.type + "_customSalutation"}
							name="customSalutation"
							value={address.customSalutation || ''}
							onChange={this.props.onChange}
							placeholder={placeholders.customSalutation}
							errors={formService.findAddressErrorForField(this.props.errors,'customSalutation', placeholders.customSalutation)}
						/>
					</>
				)}
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_companyName_label"} for={this.props.type + '_companyName'} label={placeholders.companyName}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_companyName"}
					name="companyName"
					value={address.companyName}
					onChange={this.props.onChange}
					placeholder={placeholders.companyName}
					autoComplete={`${this.props.autoCompleteType} organization`}
					errors={formService.findAddressErrorForField(this.props.errors,'companyName', placeholders.companyName)}
				/>
				{(this.props.showAdditionalDropDown && !this.props.hideAdditionalInfoForAddressForm) &&
					<>
						<LeoLabel className={"visually-hidden"} id={this.props.type + "_customer-address_additionalInfo_label"} for={this.props.type + '_customer-address_additionalInfo'} label={additionalDropDownPlaceholder}/>
						<LeoSelect
							id={this.props.type + "_customer-address_additionalInfo"}
							name="additionalInfo"
							value={address.additionalInfo ? address.additionalInfo : ''}
							valueInput={address.additionalInfo ? address.additionalInfo : ''}
							onChange={this.props.onChange}
							placeholder={additionalDropDownPlaceholder}
							errors={formService.findAddressErrorForField(this.props.errors,'additionalInfo', additionalDropDownPlaceholder.replace('*',''))}
							options={
								additionalDropDownValues.map((value) => {
									return {
										value: value,
										label: value,
										isSelected: value === address.countryCode,
									}
								})
							}
							isClearable={true}
						>
						</LeoSelect>
					</>
				}
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_firstname_label"} for={this.props.type + '_firstname'} label={placeholders.firstname}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_firstname"}
					name="firstname"
					value={address.firstname}
					onChange={this.props.onChange}
					placeholder={placeholders.firstname}
					autoComplete={`${this.props.autoCompleteType} given-name`}
					errors={formService.findAddressErrorForField(this.props.errors,'firstname', placeholders.firstname.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_lastname_label"} for={this.props.type + '_lastname'} label={placeholders.lastname}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_lastname"}
					name="lastname"
					value={address.lastname}
					onChange={this.props.onChange}
					placeholder={placeholders.lastname}
					autoComplete={`${this.props.autoCompleteType} family-name`}
					errors={formService.findAddressErrorForField(this.props.errors,'lastname', placeholders.lastname.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_phoneNumber_label"} for={this.props.type + '_phoneNumber'} label={placeholders.phoneNumber}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_phoneNumber"}
					name="phoneNumber"
					value={address.phoneNumber}
					onChange={this.props.onChange}
					placeholder={placeholders.phoneNumber}
					autoComplete={`${this.props.autoCompleteType} phone`}
					errors={formService.findAddressErrorForField(this.props.errors,'phoneNumber', placeholders.phoneNumber.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_email_label"} for={this.props.type + '_email'} label={placeholders.email}/>
				<LeoInput
					type="email"
					maxLength="255"
					id={this.props.type + "_email"}
					name="email"
					value={address.email}
					onChange={(e) => this.onEmailChange(e)}
					placeholder={placeholders.email}
					autoComplete={`${this.props.autoCompleteType} email`}
					errors={formService.findAddressErrorForField(this.props.errors,'email', placeholders.email.replace('*','').trim())}
					warn={!!this.state.eMailInfo}
					infoText={this.state.eMailInfo}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_street_label"} for={this.props.type + '_street'} label={placeholders.street}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_street"}
					name="street"
					value={address.street}
					onChange={this.props.onChange}
					placeholder={placeholders.street}
					autoComplete={`${this.props.autoCompleteType} new-address-level3`}
					errors={formService.findAddressErrorForField(this.props.errors,'street', placeholders.street.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_houseNumber_label"} for={this.props.type + '_houseNumber'} label={placeholders.houseNumber}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_houseNumber"}
					name="houseNumber"
					value={address.houseNumber}
					onChange={this.props.onChange}
					placeholder={placeholders.houseNumber}
					autoComplete={`${this.props.autoCompleteType} new-address-level4`}
					errors={formService.findAddressErrorForField(this.props.errors,'houseNumber', placeholders.houseNumber.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_postalCode_label"} for={this.props.type + '_postalCode'} label={placeholders.postalCode}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_postalCode"}
					name="postalCode"
					value={address.postalCode}
					onChange={this.props.onChange}
					placeholder={placeholders.postalCode}
					autoComplete={`${this.props.autoCompleteType} postal-code`}
					errors={formService.findAddressErrorForField(this.props.errors,'postalCode', placeholders.postalCode.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_city_label"} for={this.props.type + '_city'} label={placeholders.city}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={this.props.type + "_city"}
					name="city"
					value={address.city}
					onChange={this.props.onChange}
					placeholder={placeholders.city}
					autoComplete={`${this.props.autoCompleteType} address-level2`}
					errors={formService.findAddressErrorForField(this.props.errors,'city', placeholders.city.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={this.props.type + "_country_label"} for={this.props.type + '_countryCode'} label={placeholders.country}/>
				<LeoSelect
					id={this.props.type + "_countryCode"}
					name="countryCode"
					value={address.countryCode}
					placeholder={placeholders.country}
					valueInput={countryEnum[address.countryCode] ? countryEnum[address.countryCode] : ''}
					onChange={this.props.onChange}
					options={
						countriesList.map(([key, value]) => {
							return {
								value: key,
								label: value,
								isSelected: key === address.countryCode,
							}
						})
					}
				>
				</LeoSelect>
			</LeoForm>
		);
	}
}


const mapStateToProps = (state) => {
	const showAdditionalDropDown = state.customer.showCustomerAddressAdditionalDropDown;
	const showCustomSalutationField = state.customer.showCustomSalutationField;
	return {
		showAdditionalDropDown,
		showCustomSalutationField,
	};
};

const RadiumAddressForm = radium(AddressForm);

export default connect(
	mapStateToProps,
)(RadiumAddressForm);
